import React, { useState } from 'react';

import { PageHeader } from 'components/PageHeader';

import { Button, Divider, Spin, Typography, Card, Upload, message, notification, Tag, Popover } from 'antd';

const { Title, Paragraph } = Typography;

import { LoadingOutlined, CloseOutlined } from '@ant-design/icons';
import FeatherIcon from 'feather-icons-react';
import { ALLOWED_FILES, allowed2dForConversion, allowed3dForConversion } from '@partsbadger/utils';

import UserStore from 'stores/UserStore';

import FileStore from 'stores/FileStore';

import { ErrorRender } from '@partsbadger/library';

import { observer } from 'mobx-react';

import DisplayUploadFile from 'components/FileConversionTool/DisplayUploadFile';

const FileConversionTool: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [uploadType, setUploadType] = useState('file');
    const [showFile, setShowFile] = useState(false);

    const handleUpload3DFile = async (file: File) => {
        setLoading(true);
        await FileStore.upload2DAnd3DFile(file, '3D')
            .then(res => {
                setLoading(false);
                setShowFile(true);
            })
            .catch(err => {
                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={err} />,
                });
                setLoading(false);
            });
    };

    const handleUpload2DFile = async (file: File) => {
        setLoading(true);
        await FileStore.upload2DAnd3DFile(file, '2D')
            .then(res => {
                setLoading(false);
                setShowFile(true);
            })
            .catch(err => {
                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={err} />,
                });
                setLoading(false);
            });
    };

    return (
        <>
            <PageHeader>
                <div className="d-flex justify-content-between align-items-baseline mb-3">
                    <Title level={1}>2D and 3D File Conversion Tool</Title>
                </div>
            </PageHeader>

            <div className="container py-4">
                {showFile ? (
                    <DisplayUploadFile
                        removeUpload={() => {
                            setShowFile(false);
                        }}
                    />
                ) : (
                    <Card bordered={true} bodyStyle={{ padding: '1rem' }} className="first-step shadow-sm">
                        <Spin spinning={loading} tip={'Uploading...'} indicator={<LoadingOutlined />}>
                            <Upload.Dragger
                                name="file"
                                multiple={false}
                                showUploadList={false}
                                beforeUpload={file => {
                                    const type = file.name.split('.').pop();

                                    const isLt2M = file.size / 1024 / 1024 <= 100;

                                    if (!isLt2M) {
                                        message.error('File must be smaller than 100MB!', 10);
                                        return false;
                                    }

                                    const isAllowed3DFile = ALLOWED_FILES.allowed3d_for_conversion_tool.filter(file => {
                                        return file?.toLowerCase() === type?.toLowerCase();
                                    });

                                    const isAllowed2DFile = ALLOWED_FILES.allowed2d_for_conversion_tool.filter(file => {
                                        return file?.toLowerCase() === type?.toLowerCase();
                                    });

                                    if (isAllowed3DFile.length === 0 && isAllowed2DFile.length === 0) {
                                        message.error('The file type is not allowed, try again.', 3);
                                    }

                                    if (isAllowed3DFile.length === 1) {
                                        handleUpload3DFile(file);
                                    }

                                    if (isAllowed2DFile.length === 1) {
                                        handleUpload2DFile(file);
                                    }
                                }}
                            >
                                <div className="py-4">
                                    <p className="ant-upload-text pb-2" style={{ fontSize: '1.6rem', fontWeight: 600 }}>
                                        Select File to Convert
                                    </p>

                                    <p className="ant-upload-hint w-full m-auto">
                                        <span>Drag and drop your 2D or 3D file to convert</span>
                                    </p>

                                    <div style={{ width: '6rem', margin: '0 auto' }}>
                                        <Divider>or</Divider>
                                    </div>

                                    <Button
                                        type={'primary'}
                                        className="d-flex flex-row align-items-center mx-auto mb-2 shadow-light"
                                        icon={<FeatherIcon icon="upload-cloud" size="22" className="mr-2" />}
                                        onClick={() => setUploadType('file')}
                                    >
                                        {uploadType === 'directory' ? 'Upload Folder' : 'Upload File'}
                                    </Button>

                                    <p className="flex flex-col pt-3">
                                        <span className="text-uppercase">
                                            <strong>Convertable 2D Extensions:</strong>{' '}
                                            {allowed2dForConversion.replace(/,/g, ' ')}.
                                        </span>
                                        <span className="text-uppercase">
                                            <strong>Convertable 3D Extensions:</strong>{' '}
                                            {allowed3dForConversion.replace(/,/g, ' ')}.
                                        </span>

                                        <span>100 MB Max File Size</span>
                                    </p>
                                </div>
                            </Upload.Dragger>
                        </Spin>
                    </Card>
                )}
            </div>
        </>
    );
};

export default observer(FileConversionTool);
